import axios from 'axios';
import Cookies from 'js-cookie';

let BASE_URL_VALUE = 'https://api-grievance.airaops.com';
let AUTH_TOKEN_VALUE = null;

try {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) {
    BASE_URL_VALUE = import.meta.env.VITE_API_URL;
  } else {
    // eslint-disable-next-line no-undef
    const nodeProcess = typeof process !== 'undefined' ? process : null;
    if (nodeProcess?.env?.VITE_API_URL) {
      BASE_URL_VALUE = nodeProcess.env.VITE_API_URL;
    }
  }
} catch {
  // Ignore env resolution errors in non-Vite hosts
}

/** Runtime override for API host; updates the shared axios instance baseURL. */
export const setBaseUrl = (url) => {
  BASE_URL_VALUE = url || '';
  instance.defaults.baseURL = BASE_URL_VALUE;
};

export const setAuthToken = (token) => {
  // Keep the host-provided token in memory. Writing it to a shared `token`
  // cookie can overwrite the parent application's session and is unreliable
  // on non-HTTPS development hosts.
  AUTH_TOKEN_VALUE = token || null;
};

const getAccessToken = () => AUTH_TOKEN_VALUE || Cookies.get('token') || null;

const instance = axios.create({
  baseURL: BASE_URL_VALUE,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Resolve auth immediately before every request so token changes made by the
// parent are picked up without recreating the client or waiting for an effect.
instance.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  config.headers = config.headers || {};

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
});

let isRefreshing = false;
let refreshTokenPromise = null;

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const response = error.response;

    if (response && (response.status === 401 || response.status === 402)) {
      const refreshToken = Cookies.get('refreshToken');

      if (refreshToken) {
        try {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshTokenPromise = axios.post(
              `${BASE_URL_VALUE}/api/v1/auth/refresh`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              },
            );
          }

          const refreshResponse = await refreshTokenPromise;
          const { token: newToken, refreshToken: newRefreshToken } =
            refreshResponse.data;

          setAuthToken(newToken);
          if (newRefreshToken) {
            Cookies.set('refreshToken', newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch {
          Cookies.remove('token');
          Cookies.remove('refreshToken');
        } finally {
          isRefreshing = false;
          refreshTokenPromise = null;
        }
      }
    }

    return Promise.reject(error);
  },
);

/** Shared axios client used by package pages/hooks. Parent sets base URL + token via ApiProvider. */
export default instance;
